import React from 'react';
import styled from 'styled-components';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';

const ResultsPanel = ({ results, isProcessing, progress }) => {
  if (isProcessing) {
    return (
      <Container>
        <ProcessingState>
          <AutoAwesomeIcon style={{ fontSize: '3rem', color: '#008080', animation: 'pulse 2s ease-in-out infinite' }} />
          <ProcessingText>Analyzing your data with AI...</ProcessingText>
          <ProcessingHint>This may take a few moments</ProcessingHint>
        </ProcessingState>
      </Container>
    );
  }

  if (!results) {
    return (
      <Container>
        <EmptyState>
          <InfoIcon style={{ fontSize: '3rem', color: '#ccc', marginBottom: '1rem' }} />
          <EmptyText>Select a project and ask a question to see results</EmptyText>
          <EmptyHint>The AI will analyze your survey data and provide insights</EmptyHint>
        </EmptyState>
      </Container>
    );
  }

  if (results.error) {
    return (
      <Container>
        <ErrorState>
          <ErrorIcon style={{ fontSize: '2.5rem', color: '#dc3545', marginBottom: '1rem' }} />
          <ErrorTitle>Error Processing Query</ErrorTitle>
          <ErrorMessage>{results.error}</ErrorMessage>
        </ErrorState>
      </Container>
    );
  }

  return (
    <Container>
      {results.confidence !== undefined && (
        <ConfidenceBar confidence={results.confidence}>
          <ConfidenceLabel>Confidence:</ConfidenceLabel>
          <ConfidenceValue>{Math.round(results.confidence * 100)}%</ConfidenceValue>
          <ConfidenceFill confidence={results.confidence} />
        </ConfidenceBar>
      )}

      <Section>
        <SectionTitle>
          <CheckCircleIcon style={{ fontSize: '1.2rem', marginRight: '0.5rem', color: '#28a745' }} />
          Direct Answer
        </SectionTitle>
        <SummaryText>{results.summary || 'No direct answer available'}</SummaryText>
      </Section>

      {results.keyFindings && results.keyFindings.length > 0 && (
        <Section>
          <SectionTitle>Key Findings</SectionTitle>
          <FindingsList>
            {results.keyFindings.map((finding, index) => (
              <FindingItem key={index}>
                <FindingBullet>•</FindingBullet>
                <FindingText>{finding}</FindingText>
              </FindingItem>
            ))}
          </FindingsList>
        </Section>
      )}

      {results.dataTable && results.dataTable.length > 0 && (
        <Section>
          <SectionTitle>Data Table</SectionTitle>
          <TableContainer>
            <Table>
              <thead>
                <tr>
                  {Object.keys(results.dataTable[0]).map((header) => (
                    <Th key={header}>{header}</Th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {results.dataTable.map((row, index) => (
                  <tr key={index}>
                    {Object.values(row).map((value, idx) => (
                      <Td key={idx}>{value}</Td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableContainer>
        </Section>
      )}

      {results.recommendations && results.recommendations.length > 0 && (
        <Section>
          <SectionTitle>Recommendations</SectionTitle>
          <RecommendationsList>
            {results.recommendations.map((rec, index) => (
              <RecommendationItem key={index}>{rec}</RecommendationItem>
            ))}
          </RecommendationsList>
        </Section>
      )}

      {results.metadata && results.metadata.rowsAnalyzed && (
        <MetadataSection>
          <MetadataItem>
            <MetadataLabel>Rows Analyzed:</MetadataLabel>
            <MetadataValue>{results.metadata.rowsAnalyzed?.toLocaleString()}</MetadataValue>
          </MetadataItem>
          {results.metadata.queryType && (
            <MetadataItem>
              <MetadataLabel>Query Type:</MetadataLabel>
              <MetadataValue>{results.metadata.queryType}</MetadataValue>
            </MetadataItem>
          )}
        </MetadataSection>
      )}
    </Container>
  );
};

export default ResultsPanel;

const Container = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  min-height: 400px;

  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;

const ProcessingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  text-align: center;
`;

const ProcessingText = styled.div`
  font-size: 1.2rem;
  font-weight: 600;
  color: #333;
  margin-top: 1rem;
`;

const ProcessingHint = styled.div`
  font-size: 0.9rem;
  color: #666;
  margin-top: 0.5rem;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  text-align: center;
`;

const EmptyText = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: #666;
`;

const EmptyHint = styled.div`
  font-size: 0.9rem;
  color: #999;
  margin-top: 0.5rem;
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  text-align: center;
`;

const ErrorTitle = styled.div`
  font-size: 1.2rem;
  font-weight: 600;
  color: #dc3545;
`;

const ErrorMessage = styled.div`
  font-size: 0.95rem;
  color: #666;
  margin-top: 1rem;
  padding: 1rem;
  background: #f8d7da;
  border-radius: 8px;
  border: 1px solid #f5c6cb;
  max-width: 500px;
`;

const ConfidenceBar = styled.div`
  position: relative;
  background: #f5f5f5;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  overflow: hidden;
`;

const ConfidenceFill = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: ${(props) => props.confidence * 100}%;
  background: ${(props) =>
    props.confidence >= 0.85
      ? 'rgba(40, 167, 69, 0.2)'
      : props.confidence >= 0.70
      ? 'rgba(255, 193, 7, 0.2)'
      : 'rgba(220, 53, 69, 0.2)'};
  z-index: 0;
  transition: width 0.3s ease;
`;

const ConfidenceLabel = styled.span`
  position: relative;
  z-index: 1;
  font-size: 0.9rem;
  color: #666;
  font-weight: 600;
  margin-right: 0.5rem;
`;

const ConfidenceValue = styled.span`
  position: relative;
  z-index: 1;
  font-size: 1rem;
  font-weight: 700;
  color: #333;
`;

const Section = styled.div`
  margin-bottom: 1.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h4`
  display: flex;
  align-items: center;
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  margin: 0 0 1rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #f0f0f0;
`;

const SummaryText = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: #333;
  margin: 0;
  white-space: pre-wrap;
`;

const FindingsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const FindingItem = styled.div`
  display: flex;
  gap: 0.75rem;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 8px;
`;

const FindingBullet = styled.span`
  color: #008080;
  font-weight: 700;
  font-size: 1.2rem;
`;

const FindingText = styled.span`
  flex: 1;
  font-size: 0.95rem;
  line-height: 1.5;
  color: #333;
`;

const TableContainer = styled.div`
  overflow-x: auto;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
`;

const Th = styled.th`
  background: #f8f9fa;
  padding: 0.75rem;
  text-align: left;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #e0e0e0;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 0.75rem;
  border-bottom: 1px solid #f0f0f0;
  color: #666;
`;

const RecommendationsList = styled.ol`
  margin: 0;
  padding-left: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const RecommendationItem = styled.li`
  font-size: 0.95rem;
  line-height: 1.5;
  color: #333;
`;

const MetadataSection = styled.div`
  display: flex;
  gap: 1.5rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  margin-top: 1.5rem;
`;

const MetadataItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const MetadataLabel = styled.span`
  font-size: 0.8rem;
  color: #666;
  text-transform: uppercase;
  font-weight: 600;
`;

const MetadataValue = styled.span`
  font-size: 1rem;
  color: #333;
  font-weight: 600;
`;
